import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import { fmtDate } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Loader2, Clock, MapPin, User, Search, X, Users, Building2, AlertTriangle } from "lucide-react";

const STATO_SESS = {
  pianificata: "bg-blue-50 text-blue-700 border-blue-200",
  confermata: "bg-emerald-50 text-emerald-700 border-emerald-200",
  svolta: "bg-slate-100 text-slate-600 border-slate-200",
  annullata: "bg-rose-50 text-rose-700 border-rose-200",
  rinviata: "bg-amber-50 text-amber-700 border-amber-200",
};

const FILTRI = [
  { key: "corso", label: "Corso", opts: (s) => [[s.corso_id, `${s.corso_codice} · ${s.corso_titolo}`]], match: (s, v) => s.corso_id === v },
  { key: "sede", label: "Aula / Sede", opts: (s) => (s.sede ? [[s.sede, s.sede]] : []), match: (s, v) => s.sede === v },
  { key: "docente", label: "Docente", opts: (s) => s.docenti.map((d) => [d.id, d.nome]), match: (s, v) => s.docenti.some((d) => d.id === v) },
  { key: "persona", label: "Partecipante", opts: (s) => s.partecipanti.map((p) => [p.id, p.nome]), match: (s, v) => s.partecipanti.some((p) => p.id === v) },
  { key: "azienda", label: "Azienda", opts: (s) => s.aziende.map((a) => [a.id, a.nome]), match: (s, v) => s.aziende.some((a) => a.id === v) },
  { key: "stato", label: "Stato", opts: (s) => (s.stato ? [[s.stato, s.stato]] : []), match: (s, v) => s.stato === v },
];

const haystack = (s) => [s.corso_titolo, s.corso_codice, s.argomento, s.sede, s.modalita, s.stato,
  ...s.docenti_nomi, ...s.partecipanti.map((p) => p.nome), ...s.aziende.map((a) => a.nome)].join(" ").toLowerCase();

const time = (s) => {
  try { return new Date(s).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

function FilterBar({ sessioni, params, setParam, reset }) {
  const options = useMemo(() => Object.fromEntries(FILTRI.map((f) => {
    const m = new Map();
    sessioni.forEach((s) => f.opts(s).forEach(([v, l]) => m.set(v, l)));
    return [f.key, [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))];
  })), [sessioni]);
  const active = FILTRI.some((f) => params.get(f.key)) || params.get("q");

  return (
    <div className="mb-6 rounded-xl border bg-white p-4" data-testid="calendario-filtri">
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative w-full md:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={params.get("q") || ""} onChange={(e) => setParam("q", e.target.value)}
            placeholder="Cerca corso, codice, aula, docente, partecipante, azienda..." className="pl-9" data-testid="calendario-search" />
        </div>
        {active && (
          <Button variant="ghost" size="sm" onClick={reset} className="text-slate-500 md:ml-auto" data-testid="calendario-reset">
            <X className="mr-1 h-4 w-4" /> Azzera filtri
          </Button>
        )}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        {FILTRI.map((f) => (
          <Select key={f.key} value={params.get(f.key) || "__all"} onValueChange={(v) => setParam(f.key, v === "__all" ? "" : v)}>
            <SelectTrigger className={params.get(f.key) ? "border-[#E11D48] text-[#E11D48]" : ""} data-testid={`filtro-${f.key}`}>
              <span className="truncate text-left"><span className="text-slate-400">{f.label}:</span> {options[f.key].find(([v]) => v === params.get(f.key))?.[1] || "tutti"}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{f.label}: tutti</SelectItem>
              {options[f.key].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
      </div>
    </div>
  );
}

function SessionCard({ s }) {
  const conf = s.conflitti || [];
  return (
    <div className={`rounded-xl border bg-white p-4 transition-colors ${conf.length ? "border-rose-400 ring-1 ring-rose-200" : "hover:border-[#FDA4AF]"}`} data-testid="calendar-session">
      <div className="flex items-start justify-between gap-2">
        <Link to={`/corsi/${s.corso_id}`} className="font-medium text-slate-900 hover:text-[#E11D48] line-clamp-2">{s.corso_titolo}</Link>
        <Badge label={s.stato} cls={STATO_SESS[s.stato] || "bg-slate-100 text-slate-600 border-slate-200"} />
      </div>
      <div className="mt-1 font-mono text-xs text-slate-400">{s.corso_codice} · {s.argomento}</div>
      <div className="mt-3 space-y-1.5 text-sm text-slate-600">
        <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" /> {time(s.data_inizio)} – {time(s.data_fine)} ({s.ore}h)</div>
        <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {s.sede || "—"} · {s.modalita}</div>
        <div className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" /> {s.docenti_nomi.join(", ") || "—"}</div>
        <div className="flex items-center gap-2"><Users className="h-4 w-4 text-slate-400" /> {s.partecipanti.length} partecipanti</div>
        {s.aziende.length > 0 && <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /> <span className="line-clamp-1">{s.aziende.map((a) => a.nome).join(", ")}</span></div>}
      </div>
      {conf.length > 0 && (
        <div className="mt-3 rounded-lg bg-rose-50 p-2.5 text-xs text-rose-700" data-testid="session-conflict">
          <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Sovrapposizione orario</div>
          <ul className="mt-1 space-y-0.5">
            {conf.map((c) => (
              <li key={`${c.session_id}-${c.tipo}-${c.risorsa}`}>
                {c.tipo === "aula" ? <>L'aula <span className="font-medium">{c.risorsa}</span> è già occupata da</> : <><span className="font-medium">{c.risorsa}</span> è già in</>}{" "}
                <Link to={`/corsi/${c.corso_id}`} className="underline hover:text-rose-900">{c.corso_titolo}</Link> ({time(c.data_inizio)}–{time(c.data_fine)})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Calendario() {
  const [sessioni, setSessioni] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/calendario/sessioni").then((r) => setSessioni(r.data));
  }, []);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };

  if (!sessioni)
    return <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const q = (params.get("q") || "").trim().toLowerCase();
  const filtered = sessioni.filter((s) => FILTRI.every((f) => !params.get(f.key) || f.match(s, params.get(f.key)))
    && (!q || q.split(/\s+/).every((w) => haystack(s).includes(w)))
    && (!params.get("conflitti") || s.conflitti.length > 0));
  const nConf = sessioni.filter((s) => s.conflitti.length > 0).length;

  const groups = {};
  filtered.slice().sort((a, b) => (a.data_inizio || "").localeCompare(b.data_inizio || "")).forEach((s) => {
    const day = (s.data_inizio || "").slice(0, 10);
    (groups[day] = groups[day] || []).push(s);
  });
  const ore = filtered.reduce((t, s) => t + (s.ore || 0), 0);

  return (
    <div data-testid="calendario-page">
      <PageHeader title="Calendario Sessioni" subtitle="Docenze, aule e ore erogate per giornata" />
      <FilterBar sessioni={sessioni} params={params} setParam={setParam} reset={() => setParams({}, { replace: true })} />
      {nConf > 0 && (
        <div className="mb-4 flex flex-col gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 sm:flex-row sm:items-center sm:justify-between" data-testid="conflitti-banner">
          <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {nConf} sessioni con docenti, tutor o aule sovrapposti nello stesso orario</span>
          <Button size="sm" variant={params.get("conflitti") ? "default" : "outline"} onClick={() => setParam("conflitti", params.get("conflitti") ? "" : "1")}
            className={params.get("conflitti") ? "bg-[#E11D48] hover:bg-[#BE123C]" : "border-rose-300 text-rose-700"} data-testid="conflitti-toggle">
            {params.get("conflitti") ? "Mostra tutte" : "Mostra solo sovrapposizioni"}
          </Button>
        </div>
      )}
      <div className="mb-4 text-sm text-slate-500" data-testid="calendario-risultati">
        {filtered.length} sessioni · {Math.round(ore * 100) / 100}h{filtered.length !== sessioni.length && <span> (su {sessioni.length} totali)</span>}
      </div>
      {Object.keys(groups).length === 0 ? (
        <EmptyState title="Nessuna sessione trovata" hint="Modifica la ricerca o i filtri." />
      ) : (
        <div className="space-y-6">
          {Object.entries(groups).map(([day, sess]) => (
            <div key={day} data-testid="calendar-day">
              <div className="mb-2 flex items-center gap-2">
                <div className="font-display text-sm font-bold uppercase tracking-wider text-[#E11D48]">{fmtDate(day)}</div>
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-400">{sess.length} sessioni</span>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {sess.map((s) => <SessionCard key={s.id} s={s} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
